import React, { useEffect, useState } from 'react';
import { FormModel, Props, Resources } from './Expenditure.types';
import css from './Expenditure.module.scss';
import { Box, Columns, Icon } from 'modules/af_cm_ui_kit';
import validationSchema from './validationSchema';
import { SubPageTemplate } from '../../../partials';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { liabilityTypeDirectorFactory } from './liabilities';
import { useDispatch, useSelector } from 'react-redux';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { SummaryBox } from 'modules/cm/pages/CaseDetailsPage/partials';
import formatCurrencyValue from 'utils/formatCurrency';
import Sticky from 'components/Sticky/Sticky';
import { State } from 'modules/cm/store/cases/types';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../form/yup/reactHookFormResolver';
import { liabilitiesApi } from '../../../../../api';
import { updateApplicant, updateLoanDetails } from '../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../hooks/useSaveButtonClicked';
import { saveCase, setErrorModal } from '../../../../../store/cases/actions';
import * as subPages from '../../../../../store/subPages';
import { SUBPAGES } from '../../../../../store/subPages/types';
import CheckDirty from '../../../../../components/CheckDirty/CheckDirty';

type LiabilityItemProps = {
  onDelete: () => void;
  resources: Resources;
  modulePrefix: string;
  canDeleteLiability: boolean;
  getErrors: (fieldName: string) => string[];
  index: number;
  expenditure: any;
};

const LiabilityItem = ({
  modulePrefix,
  resources,
  onDelete,
  canDeleteLiability,
  getErrors,
  index,
  expenditure,
}: LiabilityItemProps) => {
  if (!expenditure) {
    return <></>;
  }

  const strategy = liabilityTypeDirectorFactory(expenditure.type, expenditure.id);

  const shouldShow = (field: FIELDS): boolean => {
    return strategy.getAllowedFields().includes(field);
  };

  return (
    <div className={css.wrapper}>
      <div className={css.typeWrapper}>
        <div className={css.type}>
          <UpdatableField
            fieldType={INPUTS.SELECT}
            modulePrefix={modulePrefix}
            field={{
              ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_TYPE],
              label: strategy.getTitle(FIELDS.EXPENDITURE_TYPE),
            }}
            options={resources.expenditureTypes}
            required
            getErrors={getErrors}
          />
        </div>

        <div className={css.actions}>
          <button type="button" className={css.button} onClick={() => onDelete()} disabled={canDeleteLiability}>
            <Icon type="trash" />
          </button>
        </div>
      </div>

      {expenditure.type && (
        <>
          {shouldShow(FIELDS.EXPENDITURE_BALANCE) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_BALANCE],
                label: strategy.getTitle(FIELDS.EXPENDITURE_BALANCE),
              }}
              variant={VARIANTS_TYPES.CURRENCY}
              isUpdateable={true}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_CREDIT_LIMIT) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_CREDIT_LIMIT],
                label: strategy.getTitle(FIELDS.EXPENDITURE_CREDIT_LIMIT),
              }}
              variant={VARIANTS_TYPES.CURRENCY}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_AMOUNT) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_AMOUNT],
                label: strategy.getTitle(FIELDS.EXPENDITURE_AMOUNT),
              }}
              variant={VARIANTS_TYPES.CURRENCY}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_FREQUENCY) && (
            <UpdatableField
              fieldType={INPUTS.SELECT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_FREQUENCY],
                label: strategy.getTitle(FIELDS.EXPENDITURE_FREQUENCY),
              }}
              options={resources.expenditurePaymentFrequencies}
              required
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_END_DATE) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_END_DATE],
                label: strategy.getTitle(FIELDS.EXPENDITURE_END_DATE),
              }}
              variant={VARIANTS_TYPES.DATE}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_DESCRIPTION) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_DESCRIPTION],
                label: strategy.getTitle(FIELDS.EXPENDITURE_DESCRIPTION),
              }}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES) && (
            <UpdatableField
              fieldType={INPUTS.TEXT}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES],
                label: strategy.getTitle(FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES),
              }}
              variant={VARIANTS_TYPES.NUMBER}
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN) && (
            <UpdatableField
              fieldType={INPUTS.CHECKBOX}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN],
                label: strategy.getTitle(FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN),
              }}
              variant="inline"
              getErrors={getErrors}
            />
          )}

          {shouldShow(FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN) && (
            <UpdatableField
              fieldType={INPUTS.CHECKBOX}
              modulePrefix={modulePrefix}
              field={{
                ...fields[FIELDS.EXPENDITURES_PREFIX](index.toString())[FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN],
                label: strategy.getTitle(FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN),
              }}
              variant="inline"
              getErrors={getErrors}
            />
          )}
        </>
      )}

      <hr className={css.divider} />
    </div>
  );
};

const Expenditure: React.FC<Props> = ({
  modulePrefix,
  caseId,
  applicantIndex,
  applicantId,
  applicantData,
  loanDetailsData,
  applicantInitialData,
  loanDetailsInitialData,
  resources,
  numberOfApplicants,
}) => {
  const dispatch = useDispatch();
  const isCaseReadOnly = useCaseIsReadOnly();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const defaultValues = (applicantData: any, loanDetailsData: any): FormModel => ({
    [FIELDS.EXPENDITURES_PREFIX]: applicantData ? applicantData[FIELDS.EXPENDITURES_PREFIX] : [],
    [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: applicantData[FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS],
    [FIELDS.MONTHLY_BASIC_SPEND]: loanDetailsData[FIELDS.MONTHLY_BASIC_SPEND],
    [FIELDS.ANNUAL_COUNCIL_TAX]: loanDetailsData[FIELDS.ANNUAL_COUNCIL_TAX],
    [FIELDS.ANNUAL_GROUND_RENT]: loanDetailsData[FIELDS.ANNUAL_GROUND_RENT],
  });
  const methods = useForm<FormModel>({
    mode: 'onBlur',
    defaultValues: defaultValues(applicantInitialData, loanDetailsInitialData),
    resolver: useYupValidationResolver(validationSchema),
  });

  const { control, reset } = methods;

  useEffect(() => {
    reset(defaultValues(applicantData, loanDetailsData), {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [applicantData, loanDetailsData, applicantIndex, currentApplicantIndex, reset]);

  const { fields: liabilities, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.EXPENDITURES_PREFIX,
  });

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(async () => {
      const values = methods.getValues();
      await dispatch(
        updateApplicant(
          {
            [FIELDS.EXPENDITURES_PREFIX]: values[FIELDS.EXPENDITURES_PREFIX],
            [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: values[FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS],
          },
          applicantIndex,
        ),
      );
      await dispatch(
        updateLoanDetails({
          [FIELDS.MONTHLY_BASIC_SPEND]: values[FIELDS.MONTHLY_BASIC_SPEND],
          [FIELDS.ANNUAL_COUNCIL_TAX]: values[FIELDS.ANNUAL_COUNCIL_TAX],
          [FIELDS.ANNUAL_GROUND_RENT]: values[FIELDS.ANNUAL_GROUND_RENT],
        }),
      );
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      const values = methods.getValues();
      dispatch(
        updateApplicant(
          {
            [FIELDS.EXPENDITURES_PREFIX]: values[FIELDS.EXPENDITURES_PREFIX],
            [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: values[FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS],
          },
          applicantIndex,
        ),
      );
      dispatch(
        updateLoanDetails({
          [FIELDS.MONTHLY_BASIC_SPEND]: values[FIELDS.MONTHLY_BASIC_SPEND],
          [FIELDS.ANNUAL_COUNCIL_TAX]: values[FIELDS.ANNUAL_COUNCIL_TAX],
          [FIELDS.ANNUAL_GROUND_RENT]: values[FIELDS.ANNUAL_GROUND_RENT],
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const commitments = useSelector(({ currentCase }: { currentCase: State }) => {
    const applicantReadonlyFields = currentCase.readonlyFields.applicants[applicantIndex] || {
      commitmentRemaining: {},
      commitmentToBeCleared: {},
    };
    return {
      commitmentRemaining: applicantReadonlyFields.commitmentRemaining,
      commitmentToBeCleared: applicantReadonlyFields.commitmentToBeCleared,
    };
  });

  const handleAddLiabilityButtonClick = () => {
    liabilitiesApi(caseId)(applicantId)
      .postLiability()
      .then((res) => {
        append(res.data);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const { getErrors } = usePepperError('applicants', applicantIndex);
  const { getErrors: getLoanDetailsError } = usePepperError('loanDetails');

  const getSummaryErrorMessage = (fieldName: string) => getErrors(fieldName).join(' ');

  const handleDeleteLiability = (liability: any, index: number) => {
    liabilitiesApi(caseId)(applicantId)
      .deleteLiability(liability.id)
      .then(() => {
        remove(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  return (
    <SubPageTemplate title="Expenditure" isSticky={true}>
      <Sticky top={166}>
        <SummaryBox
          data={[
            {
              title: 'Commitments to be cleared monthly amount',
              value: formatCurrencyValue(Number(commitments.commitmentToBeCleared.monthlyAmount)),
              errorMessage: getSummaryErrorMessage('commitmentToBeCleared.monthlyAmount'),
            },
            {
              title: 'Commitments to be cleared balance',
              value: formatCurrencyValue(Number(commitments.commitmentToBeCleared.balance)),
              errorMessage: getSummaryErrorMessage('commitmentToBeCleared.balance'),
            },
            {
              title: 'Commitments remaining monthly amount',
              value: formatCurrencyValue(Number(commitments.commitmentRemaining.monthlyAmount)),
              errorMessage: getSummaryErrorMessage('commitmentRemaining.monthlyAmount'),
            },
            {
              title: 'Commitments remaining balance',
              value: formatCurrencyValue(Number(commitments.commitmentRemaining.balance)),
              errorMessage: getSummaryErrorMessage('commitmentRemaining.balance'),
            },
          ]}
        />
      </Sticky>
      <br />

      <FormProvider<FormModel> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(applicantIndex, SUBPAGES.APPLICANT_EXPENDITURES, isDirty),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <>
              <Box title="Essential living costs">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={{
                    ...fields[FIELDS.MONTHLY_BASIC_SPEND],
                    label: numberOfApplicants > 1 ? 'Joint monthly costs' : 'Single monthly costs',
                  }}
                  variant={VARIANTS_TYPES.CURRENCY}
                  getErrors={getLoanDetailsError}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ANNUAL_COUNCIL_TAX]}
                  variant={VARIANTS_TYPES.CURRENCY}
                  getErrors={getLoanDetailsError}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ANNUAL_GROUND_RENT]}
                  variant={VARIANTS_TYPES.CURRENCY}
                  getErrors={getLoanDetailsError}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]}
                  variant={VARIANTS_TYPES.NUMBER}
                  getErrors={getErrors}
                />
              </Box>
              <Box title="Liabilities">
                {liabilities.map((field, index: number) => (
                  <LiabilityItem
                    key={field.key}
                    modulePrefix={modulePrefix}
                    resources={resources}
                    onDelete={() => handleDeleteLiability(field, index)}
                    canDeleteLiability={isCaseReadOnly}
                    getErrors={getErrors}
                    expenditure={methods.getValues()[FIELDS.EXPENDITURES_PREFIX][index]}
                    index={index}
                  />
                ))}
                <hr />
                <button
                  type="button"
                  className={css.button}
                  onClick={handleAddLiabilityButtonClick}
                  disabled={isCaseReadOnly}
                  style={{ marginTop: 32, marginBottom: -16 }}>
                  Add {methods.getValues()[FIELDS.EXPENDITURES_PREFIX].length ? 'another' : ''} liability
                </button>
              </Box>
            </>
            <></>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default Expenditure;
